import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
  type DocumentData,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import { FIRESTORE_COLLECTIONS, firestore } from '../firebase';
import {
  AURA_META,
  AuraType,
  buildSharedReason,
  DAY_VIBES_QUESTIONS,
} from '../data/dayVibesQuestions';
import type { DayVibesSubmission } from './useDayVibes';
import { getDayVibeDateKey, getDayVibeResultsTime } from '../utils/dayVibeDate';
import {
  buildVibeIcebreakers,
  getVibeActionLabel,
  getVibeAlignmentLabel,
  resolveVibeConnection,
  VibeAlignmentLabel,
  VibeConnectionResolution,
} from '../utils/vibeSynchronizer';
import { resolvePartnerIdentity } from '../utils/partnerIdentity';
import { isPartner } from '../utils/roles';
import { readPairScore } from '../utils/vibeGameScore';

export interface DayVibeMatch {
  id: string;
  dateKey: string;
  users: [string, string];
  userA: string;
  userB: string;
  matchPercent: number;
  sameAnswerCount: number;
  totalCompared: number;
  status: 'recommended' | 'auto_vibed';
  type: 'strong_vibe_match' | 'aura_twin';
  chatId?: string;
  createdAt: unknown;
}

export interface EnrichedDayVibeMatch extends DayVibeMatch {
  otherUserId: string;
  otherUserName: string;
  otherUserPhoto: string;
  otherUserVibeVerified: boolean;
  otherUserAuraType: AuraType;
  otherUserAuraName: string;
  sharedReason: string;
  sharedTraits: string[];
  sharedAnswerTexts: string[];
  icebreakers: string[];
  alignmentLabel: VibeAlignmentLabel;
  actionLabel: string;
  connection: VibeConnectionResolution;
}

const COL = FIRESTORE_COLLECTIONS.dayVibeMatches;
const USER_DAY_VIBES = 'dayVibes';

function getAnswerText(questionId: string, optionId: string) {
  const question = DAY_VIBES_QUESTIONS.find(item => item.id === questionId);
  const option = question?.options.find(item => item.id === optionId);
  return option?.text || optionId;
}

function getSharedAnswerTexts(
  myAnswers: Record<string, string[]>,
  theirAnswers: Record<string, string[]>,
) {
  const sharedTexts: string[] = [];
  Object.entries(myAnswers).forEach(([questionId, selected]) => {
    const theirs = theirAnswers[questionId] ?? [];
    selected.filter(optionId => theirs.includes(optionId)).forEach(optionId => {
      sharedTexts.push(getAnswerText(questionId, optionId));
    });
  });
  return [...new Set(sharedTexts)].slice(0, 4);
}

// Matches are live: one dayVibeMatches doc per pair (`users: [a, b]`), written
// at the 13:00 reveal and then whenever someone new finishes the same round
// (functions/index.js matchDayVibeSubmissionEu) — so earlier players see later
// matches while this screen is open, without replaying the game.
//
// `matchingDone` tells "still matching" apart from "matched, nobody
// compatible yet", from the server's status on the player's own response
// (queued → matching → complete | retrying). Only `complete` counts as done;
// queued/matching/retrying always show "finding your vibes". A response with
// NO status at all (submitted from an older app version) falls back to "done"
// MATCHING_GRACE_MS after results opened (13:00) or after submitting.
const MATCHING_GRACE_MS = 10 * 60 * 1000;

function toMillis(value: unknown): number | null {
  if (!value) return null;
  if (typeof value === 'string') {
    const ms = Date.parse(value);
    return Number.isNaN(ms) ? null : ms;
  }
  const maybe = value as { toMillis?: () => number; seconds?: number };
  if (typeof maybe.toMillis === 'function') return maybe.toMillis();
  if (typeof maybe.seconds === 'number') return maybe.seconds * 1000;
  return null;
}

export function useDayVibeMatches(
  userId: string | null,
  submission: DayVibesSubmission | null,
) {
  const [matches, setMatches] = useState<EnrichedDayVibeMatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stampedAt, setStampedAt] = useState<number | null>(null);
  const [serverStatus, setServerStatus] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const enrichSeq = useRef(0);

  const dateKey = getDayVibeDateKey();
  const hasTodaySubmission = Boolean(submission && (submission.dateKey ?? submission.date) === dateKey);

  const matchesQuery = useMemo(() => (userId
    ? query(
        collection(firestore, COL),
        where('users', 'array-contains', userId),
        where('dateKey', '==', dateKey),
        orderBy('matchPercent', 'desc'),
        limit(20),
      )
    : null), [userId, dateKey]);

  const enrichDocs = useCallback(async (docs: Array<QueryDocumentSnapshot<DocumentData>>): Promise<EnrichedDayVibeMatch[]> => {
    if (!userId || !submission) return [];
    // Partner accounts get no personal compatibility scores (either side).
    const viewerIdentity = await resolvePartnerIdentity(userId).catch(() => null);
    if (viewerIdentity?.kind === 'partner') return [];
    // Enrich in parallel
    const enriched = await Promise.all(
      docs.map(async (docSnap) => {
        const raw = docSnap.data() as DayVibeMatch;
        const otherUserId = raw.userA === userId ? raw.userB
          : raw.userB === userId ? raw.userA
          : (Array.isArray(raw.users) ? raw.users.find(id => id !== userId) : undefined);
        if (!otherUserId) return null;
        // Only a well-formed record for exactly this pair and round counts.
        const matchPercent = readPairScore(docSnap.id, raw as unknown as Record<string, unknown>, userId, otherUserId, dateKey);
        if (matchPercent === null) return null;
        const data = { ...raw, matchPercent };
        const matchData = { ...data, id: docSnap.id };
        const alignmentLabel = getVibeAlignmentLabel(data.matchPercent);
        if (!alignmentLabel) return null;

        try {
          // The other person's top-level dayVibeResponses doc is owner-only
          // (firestore.rules), so that read is always denied for a peer; it
          // must not sink the whole card (it used to, rendering every match
          // as "Traveler"). Their users/{uid}/dayVibes mirror is readable.
          const [profileSnap, userDayVibeSnap, responseSnap] = await Promise.all([
            getDoc(doc(firestore, FIRESTORE_COLLECTIONS.users, otherUserId)),
            getDoc(doc(firestore, FIRESTORE_COLLECTIONS.users, otherUserId, USER_DAY_VIBES, dateKey)).catch(() => null),
            getDoc(doc(firestore, FIRESTORE_COLLECTIONS.dayVibeResponses, `${otherUserId}_${dateKey}`)).catch(() => null),
          ]);

          const profile = profileSnap.data() as
            | { name?: string; photo?: string; profilePhotoURL?: string; isVibeVerified?: boolean; faceVerified?: boolean; role?: unknown }
            | undefined;
          if (isPartner(profile?.role)) return null;
          const otherUserVibeVerified = profile?.isVibeVerified === true || profile?.faceVerified === true;
          const responseDoc = userDayVibeSnap?.exists() ? userDayVibeSnap : responseSnap;
          const response = responseDoc?.data() as
            | { auraType?: AuraType; auraName?: string; answers?: Record<string, string[]>; date?: string; dateKey?: string; submitted?: boolean }
            | undefined;
          const hasTodayResponse =
            response &&
            (response.dateKey ?? response.date) === dateKey &&
            response.submitted !== false;

          const otherAuraType: AuraType = hasTodayResponse && response?.auraType ? response.auraType : 'CITY_SOUL';
          const otherAuraName = hasTodayResponse ? response?.auraName ?? AURA_META[otherAuraType].name : AURA_META[otherAuraType].name;
          const otherAnswers = hasTodayResponse ? response?.answers ?? {} : {};

          const myMeta = submission ? AURA_META[submission.auraType] : null;
          const otherMeta = AURA_META[otherAuraType];

          const sharedTraits = myMeta
            ? otherMeta.traits.filter(t => myMeta.traits.includes(t)).slice(0, 2)
            : otherMeta.traits.slice(0, 2);
          const sharedAnswerTexts = submission
            ? getSharedAnswerTexts(submission.answers as Record<string, string[]>, otherAnswers)
            : [];

          const sharedReason =
            sharedAnswerTexts.length > 0
              ? `Shared frequency: ${sharedAnswerTexts.slice(0, 2).join(' + ')}.`
              : submission && Object.keys(otherAnswers).length > 0
                ? buildSharedReason(
                    submission.answers as Record<string, string[]>,
                    otherAnswers,
                  )
                : `Your vibe alignment is ${Math.min(data.matchPercent, 99)}%.`;
          const connection = await resolveVibeConnection(userId, otherUserId);
          const icebreakers = buildVibeIcebreakers({
            similarityPercent: data.matchPercent,
            sharedAnswerTexts,
            sharedTraits,
          });

          return {
            ...matchData,
            otherUserId,
            otherUserName: profile?.name ?? 'Traveler',
            otherUserPhoto: profile?.profilePhotoURL ?? profile?.photo ?? '',
            otherUserVibeVerified,
            otherUserAuraType: otherAuraType,
            otherUserAuraName: otherAuraName,
            sharedReason: sharedReason.replace(/\bmatched\b/gi, 'aligned').replace(/\bmatch\b/gi, 'alignment'),
            sharedTraits,
            sharedAnswerTexts,
            icebreakers,
            alignmentLabel,
            actionLabel: getVibeActionLabel(alignmentLabel, connection),
            connection,
          } satisfies EnrichedDayVibeMatch;
        } catch {
          const otherIdentity = await resolvePartnerIdentity(otherUserId).catch(() => null);
          if (otherIdentity?.kind === 'partner') return null;
          const connection = await resolveVibeConnection(userId, otherUserId).catch(() => ({ status: 'none' as const }));
          const icebreakers = buildVibeIcebreakers({
            similarityPercent: data.matchPercent,
            sharedAnswerTexts: [],
            sharedTraits: [],
          });
          return {
            ...matchData,
            otherUserId,
            otherUserName: 'Traveler',
            otherUserPhoto: '',
            otherUserVibeVerified: false,
            otherUserAuraType: 'CITY_SOUL' as AuraType,
            otherUserAuraName: 'City Soul',
            sharedReason: `Your vibe alignment is ${Math.min(data.matchPercent, 99)}%.`,
            sharedTraits: [],
            sharedAnswerTexts: [],
            icebreakers,
            alignmentLabel,
            actionLabel: getVibeActionLabel(alignmentLabel, connection),
            connection,
          } satisfies EnrichedDayVibeMatch;
        }
      }),
    );

    return enriched.filter((match): match is EnrichedDayVibeMatch => Boolean(match));
  }, [userId, dateKey, submission]);

  /** Enriches the latest docs; an older, slower enrichment never overwrites a newer one. */
  const applyDocs = useCallback(async (docs: Array<QueryDocumentSnapshot<DocumentData>>) => {
    const seq = ++enrichSeq.current;
    try {
      const enriched = await enrichDocs(docs);
      if (seq === enrichSeq.current) {
        setMatches(enriched);
        setError(null);
      }
    } catch (e) {
      console.error('[Tonyte][DayVibeMatches] enrich failed', { userId, dateKey, error: e });
      if (seq === enrichSeq.current) setError('Could not load Vibe Circle.');
    } finally {
      if (seq === enrichSeq.current) setLoading(false);
    }
  }, [enrichDocs, userId, dateKey]);

  const loadMatches = useCallback(async () => {
    if (!userId || !hasTodaySubmission || !matchesQuery) {
      setMatches([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const snap = await getDocs(matchesQuery);
      await applyDocs(snap.docs);
    } catch (e) {
      console.error('[Tonyte][DayVibeMatches] load failed', { userId, dateKey, error: e });
      setError('Could not load Vibe Circle.');
      setLoading(false);
    }
  }, [userId, hasTodaySubmission, matchesQuery, applyDocs, dateKey]);

  // Live match list.
  useEffect(() => {
    if (!userId || !hasTodaySubmission || !matchesQuery) {
      setMatches([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const unsubscribe = onSnapshot(
      matchesQuery,
      snap => { void applyDocs(snap.docs); },
      listenError => {
        console.warn('[Tonyte][DayVibeMatches] live listener failed; loading once', { userId, dateKey, listenError });
        void loadMatches();
      },
    );
    return unsubscribe;
  }, [userId, hasTodaySubmission, matchesQuery, applyDocs, loadMatches, dateKey]);

  // Own response: server matching status + matchingCompletedAt.
  useEffect(() => {
    setStampedAt(null);
    setServerStatus(null);
    if (!userId || !hasTodaySubmission) return undefined;
    return onSnapshot(
      doc(firestore, FIRESTORE_COLLECTIONS.dayVibeResponses, `${userId}_${dateKey}`),
      snap => {
        const data = snap.data();
        setStampedAt(toMillis(data?.matchingCompletedAt));
        setServerStatus(typeof data?.matchingStatus === 'string' ? data.matchingStatus : null);
      },
      () => { setStampedAt(null); setServerStatus(null); },
    );
  }, [userId, dateKey, hasTodaySubmission]);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);

  // Block rules: a pair is hidden when either person blocked the other.
  // Own blocks: users/{me}/blockedUsers + blocks mirror (blockerId == me);
  // blocked BY someone: blocks mirror (blockedId == me) — the only
  // direction the rules let the blocked person read.
  const [blockedIds, setBlockedIds] = useState<Record<string, Set<string>>>({});
  useEffect(() => {
    if (!userId) {
      setBlockedIds({});
      return undefined;
    }
    const sources: Array<[string, Parameters<typeof onSnapshot>[0], (d: QueryDocumentSnapshot<DocumentData>) => string]> = [
      ['own', collection(firestore, FIRESTORE_COLLECTIONS.users, userId, 'blockedUsers'), d => d.id],
      ['ownMirror', query(collection(firestore, FIRESTORE_COLLECTIONS.blocks), where('blockerId', '==', userId)), d => String(d.data().blockedId || '')],
      ['byOthers', query(collection(firestore, FIRESTORE_COLLECTIONS.blocks), where('blockedId', '==', userId)), d => String(d.data().blockerId || '')],
    ];
    const unsubs = sources.map(([key, target, pick]) => onSnapshot(
      target as never,
      (snap: { docs: Array<QueryDocumentSnapshot<DocumentData>> }) => {
        setBlockedIds(prev => ({ ...prev, [key]: new Set(snap.docs.map(pick).filter(Boolean)) }));
      },
      () => {},
    ));
    return () => unsubs.forEach(unsub => unsub());
  }, [userId]);
  const visibleMatches = useMemo(() => {
    const blocked = new Set(Object.values(blockedIds).flatMap(set => [...set]));
    return blocked.size ? matches.filter(match => !blocked.has(match.otherUserId)) : matches;
  }, [matches, blockedIds]);

  const submittedAtMs = toMillis(submission?.submittedAt);
  const resultsOpenAt = getDayVibeResultsTime().getTime();
  const graceFrom = Math.max(resultsOpenAt, submittedAtMs ?? resultsOpenAt);
  const matchingDone = serverStatus !== null
    ? serverStatus === 'complete'
    : stampedAt !== null || (now >= resultsOpenAt && now >= graceFrom + MATCHING_GRACE_MS);
  const matchingRetrying = serverStatus === 'retrying';

  return { matches: visibleMatches, loading, error, matchingDone, matchingRetrying, reload: loadMatches };
}
